import { svg } from "./dom";

/**
 * Inline SVG, stroked with currentColor, matching the desktop app's set.
 * No icon font and no emoji: an offline-first product does not reach for a CDN,
 * and an emoji renders as somebody else's drawing on every platform.
 */
const PATHS: Record<string, string[]> = {
  grid: ["M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z"],
  masonry: ["M3 3h8v6H3zM14 3h7v10h-7zM3 12h8v9H3zM14 16h7v5h-7z"],
  list: ["M8 6h13M8 12h13M8 18h13", "M3 5h2v2H3zM3 11h2v2H3zM3 17h2v2H3z"],
  calendar: ["M3 8h18M7 3v4M17 3v4", "M4 5h16v16H4z"],
  timeline: ["M3 12h18", "M7 12V6M12 12v9M17 12V8"],
  people: ["M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2", "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8", "M22 21v-2a4 4 0 0 0-3-3.87"],
  user: ["M20 21a8 8 0 0 0-16 0", "M12 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10z"],
  card: ["M3 6h18v12H3z", "M3 10h18M7 15h4"],
  tree: ["M12 4v4M6 20v-4M18 20v-4M6 16h12", "M12 8v8"],
  album: ["M4 4h7l2 3h7v13H4z"],
  search: ["M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3"],
  menu: ["M4 7h16M4 12h16M4 17h16"],
  filter: ["M4 6h16M7 12h10M10 18h4"],
  play: ["M8 5l11 7-11 7z"],
  pause: ["M7 5h4v14H7zM13 5h4v14h-4z"],
  "skip-back": ["M6 5v14", "M18 6l-8 6 8 6z"],
  "skip-forward": ["M18 5v14", "M6 6l8 6-8 6z"],
  volume: ["M4 10h4l5-4v12l-5-4H4z", "M16 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11"],
  "volume-x": ["M4 10h4l5-4v12l-5-4H4z", "M17 10l4 4M21 10l-4 4"],
  maximize: ["M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5"],
  minimize: ["M3 8h5V3M21 8h-5V3M3 16h5v5M21 16h-5v5"],
  film: ["M3 4h18v16H3z", "M7 4v16M17 4v16M3 12h18M3 8h4M3 16h4M17 8h4M17 16h4"],
  image: ["M3 4h18v16H3z", "M7 15l4-4 3 3 2-2 5 5", "M8 9h.01"],
  book: ["M4 4h11a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3z", "M18 7h2v13H7"],
  quote: ["M7 7h4v6H7zM13 7h4v6h-4z", "M7 13c0 2 1 3 3 3M13 13c0 2 1 3 3 3"],
  info: ["M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z", "M12 11v5M12 8h.01"],
  x: ["M6 6l12 12M18 6L6 18"],
  chevron: ["M9 6l6 6-6 6"],
  camera: ["M4 7h3l2-3h6l2 3h3v13H4z", "M12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"],
  upload: ["M12 19V5", "M6 11l6-6 6 6", "M4 21h16"],
  download: ["M12 5v14", "M6 13l6 6 6-6", "M4 3h16"],
  logout: ["M15 17l5-5-5-5", "M20 12H9", "M12 3H5v18h7"],
  share: ["M4 12v8h16v-8", "M12 16V4M7 9l5-5 5 5"],
  swap: ["M7 4v13M4 14l3 3 3-3", "M17 20V7M14 10l3-3 3 3"],
  eye: ["M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z", "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"],
  clock: ["M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z", "M12 7v5l3 2"],
  // The darkroom.
  "rotate-left": ["M9 5 4 9l5 4", "M4 9h8a7 7 0 1 1-7 7"],
  "rotate-right": ["M15 5l5 4-5 4", "M20 9h-8a7 7 0 1 0 7 7"],
  sparkle: ["M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z", "M18 15l.8 2.2L21 18l-2.2.8L18 21l-.8-2.2L15 18l2.2-.8z"],
  // The lightbox's own two, as icons: at a narrow width the words "Hide from
  // the library" and "Close" were pushed off the panel entirely.
  "eye-off": [
    "M3 3l18 18",
    "M10.6 10.6a2 2 0 0 0 2.8 2.8",
    "M9.9 4.2A9.7 9.7 0 0 1 12 4c5 0 9 4.5 9 8a11 11 0 0 1-2.2 3.6",
    "M6.5 6.7A11.6 11.6 0 0 0 3 12c0 3.5 4 8 9 8a9.6 9.6 0 0 0 3.6-.7",
  ],
  palette: [
    "M12 21a9 9 0 1 1 0-18c4.9 0 9 3.4 9 7.5 0 2.5-2 4.5-4.5 4.5H15a2 2 0 0 0-1.5 3.3A1.5 1.5 0 0 1 12 21z",
    "M7.5 12.5h.01",
    "M10 8.5h.01",
    "M14.5 8h.01",
  ],
};

export function icon(name: keyof typeof PATHS | string, cls = "i"): SVGSVGElement {
  const el = svg("svg", { class: cls, viewBox: "0 0 24 24", "aria-hidden": "true" });
  for (const d of PATHS[name] ?? PATHS["info"]!) el.appendChild(svg("path", { d }));
  return el;
}
